/**
 * ============================================================================
 * router/index.tsx — React Router v6 라우트 정의 + 청크 미스 자동 회복
 * ============================================================================
 *
 * 【역할】
 *   브라우저 라우터(createBrowserRouter)의 단일 정의 파일. 비인증 영역
 *   (/login → AuthLayout)과 인증 영역(/* → RootLayout) 두 트리로 구성되며,
 *   교체 배포 시 hash 가 바뀐 chunk 요청을 잡아 로그인으로 강제 이동시키는
 *   안전장치(`lazyWithReload`, `RouteErrorBoundary`)를 포함.
 *
 * 【주요 기능】
 *   - 복용 관리 시스템 자체 화면(LoginPage / DashboardPage / EmployeePage 등)
 *     을 framework 기본 화면 대신 오버라이드
 *   - pages/* 의 모든 CRUD/Admin/Showcase/Template/Scaffold 페이지 라우트 등록
 *   - lazy() 적용 라우트는 무거운 라이브러리(ECharts/PDF/3D 등) 분리용
 *   - 청크 hash 변경(배포 직후) 시 토큰+탭 세션 정리 후 /login 강제 이동
 *
 * 【사용처】 main.tsx — `<RouterProvider router={router} />`
 * 【포팅 원본】 framework/router (vue-spring-fw `router/index.ts` 참조 — Vue Router → React Router 전환)
 * 【의존성】 react-router-dom, antd Spin, 모든 pages/* + screens/*
 */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react'
import { Spin } from 'antd'

/**
 * lazyWithReload — 동적 import 가 실패하면(주로 서비스 재기동·신규 배포로 chunk
 * hash 가 바뀌어 옛 SPA 가 더 이상 존재하지 않는 chunk URL 을 요청하는 경우)
 * 토큰·탭 세션을 정리하고 로그인 화면으로 강제 이동한다.
 *
 * 시스템이 정상이 아닐 때 사용자가 새로고침하면 일단 로그인부터 다시 시키는
 * 정책을 채택한다.
 *
 * @param factory — 동적 import 팩토리 (() => import('./X'))
 * @returns chunk miss 회복이 추가된 LazyExoticComponent
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComponentType의 props 형식이 라우트마다 달라 generic any 필요
function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory()
    } catch (err) {
      const msg = (err as Error)?.message ?? ''
      const isChunkMiss =
        /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i.test(
          msg
        )
      if (isChunkMiss) {
        try {
          localStorage.removeItem('fw.accessToken')
          localStorage.removeItem('fw.refreshToken')
          sessionStorage.removeItem('fw.tab.tabs')
          sessionStorage.removeItem('fw.tab.active')
        } catch {
          /* noop */
        }
        window.location.replace('/login')
        // 페이지 이동 도중에 throw 되어 ErrorBoundary 가 잠깐 노출되지 않도록 영원히 대기
        return new Promise(() => {}) as never
      }
      throw err
    }
  })
}
import RootLayout from '@/layout/RootLayout'
import AuthLayout from '@/layout/AuthLayout'
// 본 시스템의 LoginPage / DashboardPage 는 근태 도메인 전용으로 오버라이드 (브랜드 패널 + 근태 KPI)
import { LoginPage } from '@/screens/LoginPage'
// (근태 GUNTE 화면 EmployeePage/SchedulePage/ApprovalInboxPage(screens)은 2026-06 제거)
import CustomerListPage from '@/pages/CustomerListPage'
import CustomerListPagedPage from '@/pages/CustomerListPagedPage'
import ProductListPage from '@/pages/ProductListPage'
import InventoryListPage from '@/pages/InventoryListPage'
import InventoryDynamicPage from '@/pages/InventoryDynamicPage'
import OrderListPage from '@/pages/OrderListPage'
import OrderFormPage from '@/pages/OrderFormPage'
import SalesReportPage from '@/pages/SalesReportPage'
import TransactionSamplePage from '@/pages/TransactionSamplePage'
import ButtonShowcase from '@/pages/showcase/ButtonShowcase'
import ChartShowcase from '@/pages/showcase/ChartShowcase'
import DataShowcase from '@/pages/showcase/DataShowcase'
import EditorShowcase from '@/pages/showcase/EditorShowcase'
import FileShowcase from '@/pages/showcase/FileShowcase'
import FormShowcase from '@/pages/showcase/FormShowcase'
import MenuShowcase from '@/pages/showcase/MenuShowcase'
import MessageShowcase from '@/pages/showcase/MessageShowcase'
import PanelShowcase from '@/pages/showcase/PanelShowcase'
import OverlayShowcase from '@/pages/showcase/OverlayShowcase'
import MiscShowcase from '@/pages/showcase/MiscShowcase'
import IndexShowcase from '@/pages/showcase/IndexShowcase'
import CodeManagementPage from '@/pages/CodeManagementPage'
import MenuManagementPage from '@/pages/MenuManagementPage'
import RoleManagementPage from '@/pages/RoleManagementPage'
import UserManagementPage from '@/pages/UserManagementPage'
import AiConfigPage from '@/pages/AiConfigPage'
import MessageManagementPage from '@/pages/MessageManagementPage'
import LabelManagementPage from '@/pages/LabelManagementPage'
import LocaleManagementPage from '@/pages/LocaleManagementPage'
import RolePermissionPage from '@/pages/RolePermissionPage'
import ItemPermissionPage from '@/pages/ItemPermissionPage'
import ServicePermissionPage from '@/pages/ServicePermissionPage'
import ServiceLogPage from '@/pages/ServiceLogPage'
import ExperimentListPage from '@/pages/ExperimentListPage'
import ApprovalInboxPage from '@/pages/ApprovalInboxPage'
import ApprovalRequestPage from '@/pages/ApprovalRequestPage'
import ApprovalListPage from '@/pages/ApprovalListPage'
import ApprovalDetailPage from '@/pages/ApprovalDetailPage'
import ExperimentDetailPage from '@/pages/ExperimentDetailPage'
// MLOps Model/Pipeline/Dataset — 백엔드 API 미구현으로 제거 (라우트는 /dashboard 리다이렉트)
import FileUploadPage from '@/pages/FileUploadPage'
import AuditListPage from '@/pages/AuditListPage'
import PasswordChangePage from '@/pages/PasswordChangePage'
// Wave 19 신규 페이지
import ProductCategoryPage from '@/pages/ProductCategoryPage'
import WarehousePage from '@/pages/WarehousePage'
import InventoryTransferPage from '@/pages/InventoryTransferPage'
import InventoryStocktakingPage from '@/pages/InventoryStocktakingPage'
import NotificationTemplatePage from '@/pages/NotificationTemplatePage'

// M9: 복용 관리 (lazy — three.js / ECharts 무거운 의존성 분리)
const MedicationDashboardPage = lazyWithReload(
  () => import('@/pages/medication/MedicationDashboardPage')
)
const DeviceListPage = lazyWithReload(() => import('@/pages/medication/DeviceListPage'))
const AIInsightsPage = lazyWithReload(() => import('@/pages/medication/AIInsightsPage'))
const PatientCalendarPage = lazyWithReload(() => import('@/pages/medication/PatientCalendarPage'))
const TodaySlotBoardPage = lazyWithReload(() => import('@/pages/medication/TodaySlotBoardPage'))
const PatientListPage = lazyWithReload(() => import('@/pages/medication/PatientListPage'))
const GuardianListPage = lazyWithReload(() => import('@/pages/medication/GuardianListPage'))
const MedicationCatalogPage = lazyWithReload(
  () => import('@/pages/medication/MedicationCatalogPage')
)
const PrescriptionListPage = lazyWithReload(() => import('@/pages/medication/PrescriptionListPage'))
const PrescriptionGeneratorPage = lazyWithReload(
  () => import('@/pages/medication/PrescriptionGeneratorPage')
)
const AlertInboxPage = lazyWithReload(() => import('@/pages/medication/AlertInboxPage'))
const MedicationSchedulePage = lazyWithReload(() => import('@/pages/medication/SchedulePage'))
const IntakeHistoryPage = lazyWithReload(() => import('@/pages/medication/IntakeHistoryPage'))
const IoTGatewayPage = lazyWithReload(() => import('@/pages/medication/IoTGatewayPage'))

// 템플릿 (lazy — 무거운 라이브러리 분리)
const IndexTemplate = lazyWithReload(() => import('@/pages/templates/IndexTemplate'))
const ChartTemplate = lazyWithReload(() => import('@/pages/templates/ChartTemplate'))
const MapTemplate = lazyWithReload(() => import('@/pages/templates/MapTemplate'))
const FlowTemplate = lazyWithReload(() => import('@/pages/templates/FlowTemplate'))
const EditorTemplate = lazyWithReload(() => import('@/pages/templates/EditorTemplate'))
const MarkdownTemplate = lazyWithReload(() => import('@/pages/templates/MarkdownTemplate'))
const KanbanTemplate = lazyWithReload(() => import('@/pages/templates/KanbanTemplate'))
const CalendarTemplate = lazyWithReload(() => import('@/pages/templates/CalendarTemplate'))
const PdfViewerTemplate = lazyWithReload(() => import('@/pages/templates/PdfViewerTemplate'))
const PdfExportTemplate = lazyWithReload(() => import('@/pages/templates/PdfExportTemplate'))
const BarcodeTemplate = lazyWithReload(() => import('@/pages/templates/BarcodeTemplate'))
const ExcelImportTemplate = lazyWithReload(() => import('@/pages/templates/ExcelImportTemplate'))
const NotificationTemplate = lazyWithReload(() => import('@/pages/templates/NotificationTemplate'))
const FormWizardTemplate = lazyWithReload(() => import('@/pages/templates/FormWizardTemplate'))
const MasterSlaveTemplate = lazyWithReload(() => import('@/pages/templates/MasterSlaveTemplate'))
const AdminRoleTemplate = lazyWithReload(() => import('@/pages/templates/AdminRoleTemplate'))
const ThreeDTemplate = lazyWithReload(() => import('@/pages/templates/Template3D'))
const DiagramTemplate = lazyWithReload(() => import('@/pages/templates/DiagramTemplate'))
const ImageCropTemplate = lazyWithReload(() => import('@/pages/templates/ImageCropTemplate'))
const LargeUploadTemplate = lazyWithReload(() => import('@/pages/templates/LargeUploadTemplate'))
const DashboardJoinTemplate = lazyWithReload(
  () => import('@/pages/templates/DashboardJoinTemplate')
)

// Wave 16 Scaffold Catalog (lazy — 교육용이므로 초기 로드 제외)
const IndexScaffold = lazyWithReload(() => import('@/pages/scaffold/IndexScaffoldPage'))
const Sc01 = lazyWithReload(() => import('@/pages/scaffold/Sc01ListCrudPage'))
const Sc02 = lazyWithReload(() => import('@/pages/scaffold/Sc02PagedSearchPage'))
const Sc03 = lazyWithReload(() => import('@/pages/scaffold/Sc03SplitMasterDetailPage'))
const Sc04 = lazyWithReload(() => import('@/pages/scaffold/Sc04TabsMasterDetailPage'))
const Sc05 = lazyWithReload(() => import('@/pages/scaffold/Sc05HeaderLinesPage'))
const Sc06 = lazyWithReload(() => import('@/pages/scaffold/Sc06LookupPopupPage'))
const Sc07 = lazyWithReload(() => import('@/pages/scaffold/Sc07PopupSaveReflectPage'))
const Sc08 = lazyWithReload(() => import('@/pages/scaffold/Sc08TreeFormPage'))
const Sc09 = lazyWithReload(() => import('@/pages/scaffold/Sc09DrawerEditPage'))
const Sc10 = lazyWithReload(() => import('@/pages/scaffold/Sc10InlineEditPage'))

/** lazy 청크 로딩 동안 노출되는 중앙 정렬 antd Spin. */
const Fallback = () => (
  <div style={{ padding: 48, textAlign: 'center' }}>
    <Spin size="large" />
  </div>
)

/**
 * L — lazy 컴포넌트를 Suspense 로 감싸 element 자리에 바로 사용.
 * @param El — lazy(() => import(...)) 결과
 */
const L = (El: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<Fallback />}>
    <El />
  </Suspense>
)

/**
 * RouteErrorBoundary — 라우트 렌더링 중 잡히지 않은 예외(loader/element 에러)
 * 발생 시 토큰·탭 세션을 정리하고 로그인 페이지로 이동시킨다.
 */
function RouteErrorBoundary() {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('fw.accessToken')
      localStorage.removeItem('fw.refreshToken')
      sessionStorage.removeItem('fw.tab.tabs')
      sessionStorage.removeItem('fw.tab.active')
    } catch {
      /* noop */
    }
    if (window.location.pathname !== '/login') {
      window.location.replace('/login')
    }
  }
  return <Fallback />
}

/**
 * router — 본 SPA 의 단일 BrowserRouter 인스턴스.
 *
 * 【구조】
 *   /login (AuthLayout)
 *     └── index → LoginPage
 *   / (RootLayout — 인증 가드 포함)
 *     ├── index → /dashboard 리다이렉트
 *     ├── /dashboard, /employees, /schedule, /approval (근태 도메인)
 *     ├── /customer*, /product*, /order*, /inventory* (주문관리)
 *     ├── /experiments*, /models, /pipelines, /datasets (MLOps)
 *     ├── /approval/* (결재 워크플로우)
 *     ├── /code, /menu, /role*, /admin/* (시스템 관리)
 *     ├── /showcase/* (컴포넌트 카탈로그)
 *     ├── /templates/* (lazy 무거운 템플릿)
 *     └── /scaffold/* (lazy Wave 16 스캐폴드)
 *   /* (catch-all) → / 리다이렉트
 */
export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <AuthLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [{ index: true, element: <LoginPage /> }]
    },
    {
      path: '/',
      element: <RootLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: 'dashboard', element: L(MedicationDashboardPage) },

        // (근태 GUNTE top-level 라우트 employees/schedule/approval 은 2026-06 제거.
        //  approval 워크플로(/approval/inbox 등)는 동결 보존 — 아래 결재 블록 참조.)

        // 주문관리 (DB menu_path 기준)
        { path: 'customer', element: <CustomerListPage /> },
        { path: 'customer-paged', element: <CustomerListPagedPage /> },
        { path: 'product', element: <ProductListPage /> },
        { path: 'order', element: <OrderListPage /> },
        { path: 'order/form', element: <OrderFormPage /> },
        { path: 'order/form/:id', element: <OrderFormPage /> },

        // 웨어하우스
        { path: 'inventory', element: <InventoryListPage /> },
        { path: 'inventory-dynamic', element: <InventoryDynamicPage /> },
        { path: 'tx-sample', element: <TransactionSamplePage /> },
        // Wave 19 신규 (Wave 15 프론트 미구현 이식)
        { path: 'product-category', element: <ProductCategoryPage /> },
        { path: 'warehouse', element: <WarehousePage /> },
        { path: 'inventory-transfer', element: <InventoryTransferPage /> },
        { path: 'inventory-stocktaking', element: <InventoryStocktakingPage /> },
        { path: 'notification-template', element: <NotificationTemplatePage /> },

        // 리포트
        { path: 'sales-report', element: <SalesReportPage /> },

        // MLOps 잔재 — /models, /pipelines, /datasets 는 백엔드 API 미구현(2026-05-13 검증 FAIL).
        // 메뉴에서 노출하지 않으며 라우트는 호환성을 위해 대시보드로 리다이렉트.
        { path: 'experiments', element: <ExperimentListPage /> },
        { path: 'experiments/:id', element: <ExperimentDetailPage /> },
        { path: 'models', element: <Navigate to="/dashboard" replace /> },
        { path: 'pipelines', element: <Navigate to="/dashboard" replace /> },
        { path: 'datasets', element: <Navigate to="/dashboard" replace /> },
        { path: 'files', element: <FileUploadPage /> },

        // 결재
        { path: 'approval/inbox', element: <ApprovalInboxPage /> },
        { path: 'approval/request', element: <ApprovalRequestPage /> },
        { path: 'approval/list', element: <ApprovalListPage /> },
        { path: 'approval/:id', element: <ApprovalDetailPage /> },

        // 감사 로그
        { path: 'audit', element: <AuditListPage /> },

        // 비밀번호 변경
        { path: 'password/change', element: <PasswordChangePage /> },

        // 시스템관리
        { path: 'code', element: <CodeManagementPage /> },
        { path: 'menu', element: <MenuManagementPage /> },
        { path: 'message', element: <MessageManagementPage /> },
        { path: 'label', element: <LabelManagementPage /> },
        { path: 'admin/locale', element: <LocaleManagementPage /> },
        { path: 'service-log', element: <ServiceLogPage /> },
        { path: 'admin/user', element: <UserManagementPage /> },
        { path: 'admin/role', element: <RoleManagementPage /> },
        { path: 'admin/permission', element: <RolePermissionPage /> },
        { path: 'admin/service-perm', element: <ServicePermissionPage /> },
        { path: 'admin/item-perm', element: <ItemPermissionPage /> },
        { path: 'admin/ai-config', element: <AiConfigPage /> },

        // 컴포넌트 쇼케이스 (DB menu_path 기준)
        { path: 'showcase', element: <IndexShowcase /> },
        { path: 'showcase/form', element: <FormShowcase /> },
        { path: 'showcase/button', element: <ButtonShowcase /> },
        { path: 'showcase/data', element: <DataShowcase /> },
        { path: 'showcase/panel', element: <PanelShowcase /> },
        { path: 'showcase/overlay', element: <OverlayShowcase /> },
        { path: 'showcase/menu', element: <MenuShowcase /> },
        { path: 'showcase/message', element: <MessageShowcase /> },
        { path: 'showcase/misc', element: <MiscShowcase /> },
        { path: 'showcase/file', element: <FileShowcase /> },
        { path: 'showcase/chart', element: <ChartShowcase /> },
        { path: 'showcase/editor', element: <EditorShowcase /> },

        // 템플릿 (DB menu_path 기준)
        { path: 'templates', element: L(IndexTemplate) },
        { path: 'templates/chart', element: L(ChartTemplate) },
        { path: 'templates/map', element: L(MapTemplate) },
        { path: 'templates/flow', element: L(FlowTemplate) },
        { path: 'templates/editor', element: L(EditorTemplate) },
        { path: 'templates/markdown', element: L(MarkdownTemplate) },
        { path: 'templates/kanban', element: L(KanbanTemplate) },
        { path: 'templates/calendar', element: L(CalendarTemplate) },
        { path: 'templates/pdf', element: L(PdfViewerTemplate) },
        { path: 'templates/pdf-export', element: L(PdfExportTemplate) },
        { path: 'templates/barcode', element: L(BarcodeTemplate) },
        { path: 'templates/excel-import', element: L(ExcelImportTemplate) },
        { path: 'templates/notification', element: L(NotificationTemplate) },
        { path: 'templates/form-wizard', element: L(FormWizardTemplate) },
        { path: 'templates/master-slave', element: L(MasterSlaveTemplate) },
        { path: 'templates/admin-role', element: L(AdminRoleTemplate) },
        { path: 'templates/3d', element: L(ThreeDTemplate) },
        { path: 'templates/diagram', element: L(DiagramTemplate) },
        { path: 'templates/image-crop', element: L(ImageCropTemplate) },
        { path: 'templates/large-upload', element: L(LargeUploadTemplate) },
        { path: 'templates/dashboard-join', element: L(DashboardJoinTemplate) },

        // Wave 16 Scaffold Catalog
        { path: 'scaffold', element: L(IndexScaffold) },
        { path: 'scaffold/list-crud', element: L(Sc01) },
        { path: 'scaffold/paged-search', element: L(Sc02) },
        { path: 'scaffold/split-master-detail', element: L(Sc03) },
        { path: 'scaffold/tabs-master-detail', element: L(Sc04) },
        { path: 'scaffold/header-lines', element: L(Sc05) },
        { path: 'scaffold/lookup-popup', element: L(Sc06) },
        { path: 'scaffold/popup-save-reflect', element: L(Sc07) },
        { path: 'scaffold/tree-form', element: L(Sc08) },
        { path: 'scaffold/drawer-edit', element: L(Sc09) },
        { path: 'scaffold/inline-edit', element: L(Sc10) },

        // ── M9: 복용 관리 라우트 — 복용 대시보드는 /dashboard 로 통합 ──
        { path: 'medication/today-board', element: L(TodaySlotBoardPage) },
        { path: 'medication/calendar', element: L(PatientCalendarPage) },
        { path: 'medication/patients', element: L(PatientListPage) },
        { path: 'medication/guardians', element: L(GuardianListPage) },
        { path: 'medication/devices', element: L(DeviceListPage) },
        { path: 'medication/medications', element: L(MedicationCatalogPage) },
        { path: 'medication/prescriptions', element: L(PrescriptionListPage) },
        { path: 'medication/prescriptions/generator', element: L(PrescriptionGeneratorPage) },
        { path: 'medication/schedules', element: L(MedicationSchedulePage) },
        { path: 'medication/intake', element: L(IntakeHistoryPage) },
        { path: 'medication/alerts', element: L(AlertInboxPage) },
        { path: 'medication/iot-gateway', element: L(IoTGatewayPage) },
        { path: 'medication/ai-insights', element: L(AIInsightsPage) }
      ]
    },
    { path: '*', element: <Navigate to="/" replace /> }
  ],
  {
    // V12-W2: React Router v7 deprecation warning 해소.
    // v7_startTransition 은 RouterProvider 전용 flag — main.tsx 에서 전달 (router-level 타입에 없음).
    future: {
      v7_relativeSplatPath: true
    }
  }
)
